import * as React from 'react';
import { IgcChatComponent as Component } from 'igniteui-webcomponents';
import type { IgcChatComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * A chat UI component for displaying messages, attachments, and input interaction.
 * @slot prefix - Slot for injecting content (e.g., avatar or icon) before the chat title.
 * @slot title - Slot for overriding the chat title content.
 * @slot actions - Slot for injecting header actions (e.g., buttons, menus).
 * @slot suggestions-header - Slot for rendering a custom header for the suggestions list.
 * @slot suggestions - Slot for rendering a custom list of quick reply suggestions.
 * @slot suggestions-actions - Slot for rendering additional actions.
 * @slot suggestion - Slot for rendering a single suggestion item.
 * @slot empty-state - Slot shown when there are no messages.
 * @slot typing-indicator - Slot for the "is typing" indicator.
 * @fires igcMessageCreated - Dispatched when a new chat message is created (sent).
 * @fires igcMessageReact - Dispatched when a message is reacted to.
 * @fires igcAttachmentClick - Dispatched when a chat message attachment is clicked.
 * @fires igcAttachmentAdded - Dispatched when attachment(s) are added either through drag & drop or through the default file input.
 * @fires igcAttachmentRemoved - Dispatched when an attachment is removed by the user.
 * @fires igcAttachmentDrag - Dispatched during an attachment drag operation.
 * @fires igcAttachmentDrop - Dispatched when an attachment is dropped (e.g., in a drag-and-drop operation).
 * @fires igcTypingChange - Dispatched when the typing status changes (e.g., user starts or stops typing).
 * @fires igcInputFocus - Dispatched when the chat input field gains focus.
 * @fires igcInputBlur - Dispatched when the chat input field loses focus.
 * @fires igcInputChange - Dispatched when the content of the chat input changes.
 * @csspart chat-container - Styles the main chat container.
 * @csspart header - Styles the chat header container.
 * @csspart prefix - Styles the element before the chat title (e.g., avatar).
 * @csspart title - Styles the chat header title.
 * @csspart message-area-container - Styles the container holding the messages and (optional) suggestions.
 * @csspart message-list - Styles the message list container.
 * @csspart message-item - Styles each message wrapper.
 * @csspart typing-indicator - Styles the typing indicator container.
 * @csspart typing-dot - Styles individual typing indicator dots.
 * @csspart suggestions-container - Styles the container holding all suggestions.
 * @csspart suggestions-header - Styles the suggestions header.
 * @csspart suggestion - Styles each suggestion item.
 * @csspart suggestion-prefix - Styles the icon or prefix in a suggestion.
 * @csspart suggestion-title - Styles the text/title of a suggestion.
 * @csspart empty-state - Styles the empty state container when there are no messages.
 * @csspart input-area-container - Styles the wrapper around the chat input area.
 * @csspart input-area - Styles the main text input area.
 * @csspart input-attachments-container - Styles the container for attachments in the input.
 * @csspart input-attachment-container - Styles a single attachment in the input area.
 * @csspart input-attachment-name - Styles the file name of an attachment.
 * @csspart input-attachment-icon - Styles the icon of an attachment.
 * @csspart text-input - Styles the text input field for typing messages.
 * @csspart input-actions-container - Styles the container for input actions.
 * @csspart input-actions-start - Styles the group of actions at the start of the input after the default file upload.
 * @csspart input-actions-end - Styles the group of actions at the end of the input.
 * @csspart file-upload-container - Styles the container for the file upload input.
 * @csspart file-upload - Styles the file upload input itself.
 * @csspart send-button-container - Styles the container around the send button.
 * @csspart send-button - Styles the send button.
 * @csspart message-container - Styles the container of a single message.
 * @csspart message-list - (forwarded) - Styles the internal list of messages.
 * @csspart message-header - Styles the header of a message (e.g., sender, timestamp).
 * @csspart message-content - Styles the text content of a message.
 * @csspart message-attachments-container - Styles the container for message attachments.
 * @csspart message-attachment - Styles a single message attachment.
 * @csspart message-actions-container - Styles the container holding message actions.
 * @csspart message-sent - Styles messages marked as sent by the current user.
 * @csspart attachment-header - Styles the header of an attachment block.
 * @csspart attachment-content - Styles the content of an attachment block.
 * @csspart attachment-icon - Styles the icon of an attachment.
 * @csspart file-name - Styles the file name shown in an attachment.
 * @class
 */
export const IgrChat = /* @__PURE__ */ createComponent({
  tagName: 'igc-chat',
  displayName: 'IgrChat',
  elementClass: Component,
  react: React,
  events: {
    onMessageCreated: 'igcMessageCreated' as EventName<
      IgcChatComponentEventMap['igcMessageCreated']
    >,
    onMessageReact: 'igcMessageReact' as EventName<
      IgcChatComponentEventMap['igcMessageReact']
    >,
    onAttachmentClick: 'igcAttachmentClick' as EventName<
      IgcChatComponentEventMap['igcAttachmentClick']
    >,
    onAttachmentAdded: 'igcAttachmentAdded' as EventName<
      IgcChatComponentEventMap['igcAttachmentAdded']
    >,
    onAttachmentRemoved: 'igcAttachmentRemoved' as EventName<
      IgcChatComponentEventMap['igcAttachmentRemoved']
    >,
    onAttachmentDrag: 'igcAttachmentDrag' as EventName<
      IgcChatComponentEventMap['igcAttachmentDrag']
    >,
    onAttachmentDrop: 'igcAttachmentDrop' as EventName<
      IgcChatComponentEventMap['igcAttachmentDrop']
    >,
    onTypingChange: 'igcTypingChange' as EventName<
      IgcChatComponentEventMap['igcTypingChange']
    >,
    onInputFocus: 'igcInputFocus' as EventName<
      IgcChatComponentEventMap['igcInputFocus']
    >,
    onInputBlur: 'igcInputBlur' as EventName<
      IgcChatComponentEventMap['igcInputBlur']
    >,
    onInputChange: 'igcInputChange' as EventName<
      IgcChatComponentEventMap['igcInputChange']
    >,
  },
  renderProps: {
    options: {
      renderers: {
        attachment: 'attachment',
        attachmentContent: 'attachmentContent',
        attachmentHeader: 'attachmentHeader',
        fileUploadButton: 'fileUploadButton',
        input: 'input',
        inputActions: 'inputActions',
        inputActionsEnd: 'inputActionsEnd',
        inputActionsStart: 'inputActionsStart',
        inputAttachments: 'inputAttachments',
        message: 'message',
        messageActions: 'messageActions',
        messageAttachments: 'messageAttachments',
        messageContent: 'messageContent',
        messageHeader: 'messageHeader',
        typingIndicator: 'typingIndicator',
        sendButton: 'sendButton',
        suggestionPrefix: 'suggestionPrefix',
      },
    },
  },
});

export type IgrChat = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrChatModule = Component;
